import { useCallback, useEffect, useMemo, useState } from 'react';
import Select from '@atlaskit/select';
import { DatePicker } from '@atlaskit/datetime-picker';
import { useFlags } from '@atlaskit/flag';
import Lozenge from '@atlaskit/lozenge';
import 'chart.js/auto';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { ArrowUpRight, Inbox, Truck } from 'react-feather';
import DeliveryRequestReportsApi, {
  ExportShape,
  IBreakdownRow,
  IDeliveryRequestReport,
} from 'core/api/delivery-request-reports/delivery-request-reports-api.service';
import { SelectOption } from 'core/utilities/interface-helpers';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import SharedButton from 'shared/components/buttons/button';
import SharedTabButton from 'shared/components/buttons/tab-button/tab-button';
import SharedTable from 'shared/components/table/table';
import { ISharedTableColumn, ISharedTableRow } from 'shared/components/table/table-interface';
import SkeletonElement from 'shared/components/layout/skeleton-element';
import {
  IReportRange,
  RangePreset,
  describeRange,
  isRangeValid,
  rangeFor,
  rangeForFilename,
  rangePresetOptions,
} from './report-range';

/**
 * Where the inbound demand went, and what that means.
 *
 * Section 03 of the proposal sells three things: volume by request type,
 * in-house versus forwarded, and a missed-opportunity view — "the share
 * resolved by third parties, and so the revenue potentially going elsewhere".
 * The missed-opportunity number is the one the system was bought for, so it is
 * the largest thing on the screen rather than a figure to be worked out from
 * two others.
 *
 * No money is attached to it. Showing revenue would mean holding a value per
 * request, which we do not, and inventing an average across a patient courier
 * run and a bulk equipment move would produce a confident number that is
 * wrong. Counts and share are defensible; a made-up total is not.
 *
 * Every figure is counted in the backend over the whole date range, not in the
 * browser over a loaded page — see the API service.
 */

/** Matches the portal's palette, with forwarded work in amber as work leaving. */
const COLOURS = {
  inHouse: '#007287',
  forwarded: '#F59E0B',
  closed: '#9CA3AF',
  awaiting: '#0077B6',
  unreadable: '#EF4444',
};

enum DemandView {
  COLLECTION = 'collection',
  DESTINATION = 'destination',
  DEPARTMENT = 'department',
}

const demandTabs = [
  { key: DemandView.COLLECTION, label: 'Collection site' },
  { key: DemandView.DESTINATION, label: 'Destination site' },
  { key: DemandView.DEPARTMENT, label: 'Department' },
];

/** Enough rows to see the shape of it without a chart nobody can read. */
const CHART_ROWS = 8;

const BREAKDOWN_COLUMNS: ISharedTableColumn[] = [
  { key: 'label', label: 'Where', templateId: 'label', width: 40 },
  { key: 'total', label: 'Requests', templateId: 'total', width: 15 },
  { key: 'inHouse', label: 'In-house', templateId: 'inHouse', width: 15 },
  { key: 'forwarded', label: 'Forwarded', templateId: 'forwarded', width: 15 },
  { key: 'share', label: 'Forwarded share', templateId: 'share', width: 15 },
];

const TARGET_COLUMNS: ISharedTableColumn[] = [
  { key: 'label', label: 'Team or supplier', templateId: 'label', width: 40 },
  { key: 'email', label: 'Sent to', templateId: 'email', width: 30 },
  { key: 'total', label: 'Requests', templateId: 'total', width: 15 },
  { key: 'share', label: 'Share of forwarded', templateId: 'share', width: 15 },
];

const percent = (share: number): string => `${(share * 100).toFixed(1)}%`;

/** Share of a breakdown row's resolved work that left the building. */
const forwardedShare = (row: IBreakdownRow): number => {
  const resolved = row.inHouse + row.forwarded;
  return resolved ? row.forwarded / resolved : 0;
};

const DeliveryRequestReport = () => {
  const flags = useFlags();

  const [preset, setPreset] = useState<SelectOption>(
    rangePresetOptions.find((option) => option.value === RangePreset.THIS_MONTH)!,
  );
  const [range, setRange] = useState<IReportRange>(rangeFor(RangePreset.THIS_MONTH));
  const [report, setReport] = useState<IDeliveryRequestReport>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [exporting, setExporting] = useState<ExportShape>();
  const [demandView, setDemandView] = useState<string>(DemandView.COLLECTION);

  const custom = preset.value === RangePreset.CUSTOM;

  const load = useCallback(async () => {
    if (!isRangeValid(range)) {
      setError('The end date is before the start date.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(undefined);
    try {
      setReport(await DeliveryRequestReportsApi.get(range.startDate, range.endDate));
    } catch (caught) {
      // Kept on screen rather than only flagged: a dashboard that silently
      // shows nothing looks like a quiet month rather than a failure.
      setError((caught as Error).message || 'The report could not be loaded.');
      setReport(undefined);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const choosePreset = (option: SelectOption) => {
    setPreset(option);
    if (option.value !== RangePreset.CUSTOM) setRange(rangeFor(option.value as RangePreset));
  };

  const download = async (shape: ExportShape) => {
    setExporting(shape);
    try {
      const csv = await DeliveryRequestReportsApi.exportCsv(range.startDate, range.endDate, shape);
      // The same download as every other export in the portal, byte-order mark
      // included so Excel opens it as UTF-8.
      const blob = new Blob(['\ufeff', csv]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `d_hive_delivery_requests_${shape}_${rangeForFilename(range)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showSuccessFlag('Export ready', 'The spreadsheet has been downloaded.', flags);
    } catch (caught) {
      showErrorFlag('Export failed', (caught as Error).message || 'The export failed, please try again.', flags);
    } finally {
      setExporting(undefined);
    }
  };

  const totals = report?.totals;
  const missed = report?.missedOpportunity;

  const headlines = useMemo(
    () => [
      {
        key: 'received',
        label: 'Requests received',
        value: totals?.total ?? 0,
        colour: 'bg-secondary',
        icon: <Inbox size={20} color='white' />,
      },
      {
        key: 'inHouse',
        label: 'Handled in-house',
        value: totals?.inHouse ?? 0,
        colour: 'bg-primary',
        icon: <Truck size={20} color='white' />,
      },
      {
        key: 'forwarded',
        label: 'Forwarded on',
        value: totals?.forwarded ?? 0,
        colour: 'bg-amber-500',
        icon: <ArrowUpRight size={20} color='white' />,
      },
    ],
    [totals],
  );

  /** The secondary counts, which explain the difference between the headlines. */
  const asides = useMemo(
    () => [
      { key: 'awaiting', label: 'Still waiting', value: totals?.awaiting ?? 0 },
      { key: 'closed', label: 'Closed', value: totals?.closed ?? 0 },
      { key: 'unreadable', label: 'Could not be read', value: totals?.unreadable ?? 0 },
    ],
    [totals],
  );

  const outcomeDoughnut = useMemo(
    () => ({
      labels: ['Handled in-house', 'Forwarded on'],
      datasets: [
        {
          data: [missed?.inHouse ?? 0, missed?.forwarded ?? 0],
          backgroundColor: [COLOURS.inHouse, COLOURS.forwarded],
          borderWidth: 0,
        },
      ],
    }),
    [missed],
  );

  /**
   * Volume by type, stacked by what happened to it.
   *
   * Stacked rather than a plain total because the interesting thing about a
   * request type is not how much of it arrives but how much of it we keep.
   */
  const typeChart = useMemo(() => {
    const rows = (report?.byRequestType ?? []).slice(0, CHART_ROWS);
    return {
      labels: rows.map((row) => row.label),
      datasets: [
        {
          label: 'Handled in-house',
          data: rows.map((row) => row.inHouse),
          backgroundColor: COLOURS.inHouse,
        },
        {
          label: 'Forwarded on',
          data: rows.map((row) => row.forwarded),
          backgroundColor: COLOURS.forwarded,
        },
        {
          label: 'Other',
          data: rows.map((row) => row.closed + row.awaiting + row.unreadable),
          backgroundColor: COLOURS.closed,
        },
      ],
    };
  }, [report]);

  const overTimeChart = useMemo(() => {
    const rows = report?.overTime ?? [];
    return {
      labels: rows.map((row) => row.label),
      datasets: [
        {
          label: 'Handled in-house',
          data: rows.map((row) => row.inHouse),
          borderColor: COLOURS.inHouse,
          backgroundColor: COLOURS.inHouse,
          tension: 0.3,
        },
        {
          label: 'Forwarded on',
          data: rows.map((row) => row.forwarded),
          borderColor: COLOURS.forwarded,
          backgroundColor: COLOURS.forwarded,
          tension: 0.3,
        },
        {
          label: 'Received',
          data: rows.map((row) => row.total),
          borderColor: COLOURS.awaiting,
          backgroundColor: COLOURS.awaiting,
          borderDash: [4, 4],
          tension: 0.3,
        },
      ],
    };
  }, [report]);

  const demandRows: IBreakdownRow[] = useMemo(() => {
    if (!report) return [];
    if (demandView === DemandView.DESTINATION) return report.byDestinationSite;
    if (demandView === DemandView.DEPARTMENT) return report.byDepartment;
    return report.byCollectionSite;
  }, [report, demandView]);

  const stackedOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { stacked: true, grid: { display: false } },
      y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
    },
    plugins: { legend: { position: 'bottom' as const } },
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
    plugins: { legend: { position: 'bottom' as const } },
  };

  const card = 'bg-white rounded-md shadow-md';

  const breakdownTemplates = [
    { id: 'label', template: (row: IBreakdownRow) => <p>{row.label}</p> },
    { id: 'total', template: (row: IBreakdownRow) => <p>{row.total}</p> },
    { id: 'inHouse', template: (row: IBreakdownRow) => <p>{row.inHouse}</p> },
    { id: 'forwarded', template: (row: IBreakdownRow) => <p>{row.forwarded}</p> },
    {
      id: 'share',
      template: (row: IBreakdownRow) =>
        row.inHouse + row.forwarded ? (
          <Lozenge appearance={forwardedShare(row) >= 0.5 ? 'removed' : 'default'}>
            {percent(forwardedShare(row))}
          </Lozenge>
        ) : (
          <p className='text-gray-500'>—</p>
        ),
    },
  ];

  const breakdownRows: ISharedTableRow<IBreakdownRow>[] = demandRows.map((row) => ({
    key: row.label,
    data: row,
  }));

  const targetRows = (report?.byForwardTarget ?? []).map((row) => ({ key: row.email, data: row }));

  return (
    <div className='pb-10'>
      {/* Controls and the headline counts, in one card: the numbers mean
          nothing without the range they were counted over. */}
      <div className={`${card} my-4`}>
        <div className='flex flex-wrap items-center justify-between gap-4 border-b p-4'>
          <div className='flex flex-wrap items-center gap-4'>
            <div className='flex items-center'>
              <p className='headline-05'>Time frame</p>
              <div className='ml-4 w-[200px]'>
                <Select<SelectOption>
                  value={preset}
                  options={rangePresetOptions}
                  onChange={(value) => choosePreset(value!)}
                />
              </div>
            </div>

            {custom && (
              <div className='flex items-center gap-2' data-testid='custom-range'>
                <div className='w-[160px]' data-testid='report-start-date'>
                  <DatePicker
                    id='report-start-date'
                    locale='en-UK'
                    value={range.startDate}
                    onChange={(value) => setRange((current) => ({ ...current, startDate: value }))}
                  />
                </div>
                <p className='body-02 text-gray-500'>to</p>
                <div className='w-[160px]' data-testid='report-end-date'>
                  <DatePicker
                    id='report-end-date'
                    locale='en-UK'
                    value={range.endDate}
                    onChange={(value) => setRange((current) => ({ ...current, endDate: value }))}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Two exports rather than a dialog asking for the range again: the
              range is already chosen on this screen, and the spreadsheet is
              of what is on it. */}
          <div className='flex items-center gap-2'>
            <SharedButton
              type='button'
              appearance='default'
              label='Export requests'
              testId='export-requests'
              isLoading={exporting === 'requests'}
              disabled={!!exporting || loading}
              onClick={() => download('requests')}
            />
            <SharedButton
              type='button'
              appearance='primary'
              label='Export summary'
              testId='export-summary'
              isLoading={exporting === 'summary'}
              disabled={!!exporting || loading}
              onClick={() => download('summary')}
            />
          </div>
        </div>

        {error ? (
          <div className='p-8 text-center' data-testid='report-error'>
            <p className='body-01 text-red-700'>{error}</p>
            <div className='mt-4 flex justify-center'>
              <SharedButton type='button' appearance='default' label='Try again' onClick={load} />
            </div>
          </div>
        ) : (
          <>
            <div className='grid grid-cols-3 gap-4'>
              {headlines.map(({ key, label, value, colour, icon }) => (
                <div className='border-r py-4 pr-4 first:pl-4 last:border-0' key={key}>
                  <div className={`body-02 flex items-center rounded-lg p-3 text-white ${colour}`}>
                    {icon}
                    <p className='ml-2'>{label}</p>
                  </div>
                  {loading ? (
                    <SkeletonElement height='60px' width='74px' className='mt-2' />
                  ) : (
                    <p className='pt-2 text-6xl font-light' data-testid={`headline-${key}`}>
                      {value}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className='grid grid-cols-3 gap-4 border-t'>
              {asides.map(({ key, label, value }) => (
                <div className='border-r p-4 last:border-0' key={key}>
                  <p className='body-02 text-gray-500'>{label}</p>
                  {loading ? (
                    <SkeletonElement height='28px' width='40px' className='mt-1' />
                  ) : (
                    <p className='headline-04' data-testid={`aside-${key}`}>
                      {value}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {!error && !loading && report && (
        <>
          {report.totals.total === 0 ? (
            <div className={`${card} p-12 text-center`} data-testid='report-empty'>
              <p className='headline-05'>No requests arrived in this period</p>
              <p className='body-01 mt-2 text-gray-500'>
                Nothing came in between {describeRange(range)}. Try a wider time frame.
              </p>
            </div>
          ) : (
            <>
              <div className='grid grid-cols-2 gap-4'>
                {/* The number the system was sold on. Given the most space on
                    the screen because it is the one that starts a conversation. */}
                <div className={card} data-testid='missed-opportunity'>
                  <div className='border-b p-4'>
                    <p className='headline-05'>Missed opportunity</p>
                    <p className='body-02 mt-1 text-gray-500'>
                      Of the {report.missedOpportunity.resolved} request
                      {report.missedOpportunity.resolved === 1 ? '' : 's'} resolved in this period, the share that
                      somebody else did.
                    </p>
                  </div>
                  <div className='flex items-center gap-6 p-6'>
                    <div>
                      <p className='text-6xl font-light text-amber-600' data-testid='forwarded-share'>
                        {percent(report.missedOpportunity.forwardedShare)}
                      </p>
                      <p className='body-02 mt-2 text-gray-600'>
                        {report.missedOpportunity.forwarded} of {report.missedOpportunity.resolved} went to a third
                        party
                      </p>
                      <p className='body-02 mt-4 text-gray-500'>
                        {report.missedOpportunity.inHouse} handled in-house
                      </p>
                    </div>
                    <div className='ml-auto h-[180px] w-[180px]'>
                      <Doughnut
                        data={outcomeDoughnut}
                        options={{ maintainAspectRatio: false, plugins: { legend: { display: false } } }}
                      />
                    </div>
                  </div>
                </div>

                <div className={card} data-testid='by-request-type'>
                  <div className='border-b p-4'>
                    <p className='headline-05'>Volume by request type</p>
                    <p className='body-02 mt-1 text-gray-500'>
                      What is coming in, and how much of each type we keep.
                    </p>
                  </div>
                  <div className='h-[280px] p-4'>
                    <Bar data={typeChart} options={stackedOptions} />
                  </div>
                </div>
              </div>

              <div className={`${card} my-4`} data-testid='over-time'>
                <div className='border-b p-4'>
                  <p className='headline-05'>Volume over time</p>
                  <p className='body-02 mt-1 text-gray-500'>
                    Requests received {report.range.granularity === 'week' ? 'each week' : 'each day'} across{' '}
                    {describeRange(range)}.
                  </p>
                </div>
                <div className='h-[320px] p-4'>
                  <Line data={overTimeChart} options={lineOptions} />
                </div>
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div className={card} data-testid='by-forward-target'>
                  <div className='border-b p-4'>
                    <p className='headline-05'>Where forwarded work went</p>
                    <p className='body-02 mt-1 text-gray-500'>
                      The teams and suppliers absorbing the work, busiest first.
                    </p>
                  </div>
                  {targetRows.length ? (
                    <SharedTable
                      columns={TARGET_COLUMNS}
                      rows={targetRows}
                      customTemplates={[
                        { id: 'label', template: (row: { label: string }) => <p>{row.label}</p> },
                        {
                          id: 'email',
                          template: (row: { email: string }) => <p className='text-gray-500'>{row.email}</p>,
                        },
                        { id: 'total', template: (row: { total: number }) => <p>{row.total}</p> },
                        {
                          id: 'share',
                          template: (row: { share: number }) => <p>{percent(row.share)}</p>,
                        },
                      ]}
                    />
                  ) : (
                    <p className='body-01 p-8 text-center text-gray-500'>
                      Nothing was forwarded in this period.
                    </p>
                  )}
                </div>

                <div className={card} data-testid='by-demand'>
                  <div className='border-b p-4'>
                    <p className='headline-05'>Where demand comes from</p>
                    <p className='body-02 mt-1 text-gray-500'>
                      Which parts of the trust are generating the requests.
                    </p>
                    <div className='relative mt-4'>
                      <div className='relative z-10 flex'>
                        {demandTabs.map((tab) => (
                          <SharedTabButton
                            key={tab.key}
                            changeTab={setDemandView}
                            tab={tab}
                            currentTab={demandView}
                            testId={`demand-tab-${tab.key}`}
                          />
                        ))}
                      </div>
                      <div className='absolute bottom-0 left-0 right-0 h-px bg-gray-200' />
                    </div>
                  </div>
                  <SharedTable
                    columns={BREAKDOWN_COLUMNS}
                    rows={breakdownRows}
                    customTemplates={breakdownTemplates}
                  />
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default DeliveryRequestReport;
